/**
 * Kaktus Wishlist bridge only.
 * Theme wishlist storage / hearts / page UI have been removed — the app owns the list.
 */

/**
 * Open the Kaktus wishlist panel from the theme header heart.
 * @returns {boolean}
 */
function openKaktusWishlist() {
  const preferred = document.querySelector(
    '.header__icons[data-kaktus-header-slot] .kaktus-w-wishlist-button, .header__icons[data-kaktus-header-slot] button, .header__icons[data-kaktus-header-slot] a'
  );
  if (preferred instanceof HTMLElement) {
    preferred.click();
    return true;
  }

  const triggers = [
    ...document.querySelectorAll(
      [
        '.kaktus-w-wishlist-button',
        '[id*="kaktus"] button',
        '[id*="kaktus"] a',
        '[class*="kaktus"] button',
        '[class*="kaktus"] a',
        '[class*="kaktus-w-"]',
      ].join(', ')
    ),
  ].filter((el) => el instanceof HTMLElement);

  for (const el of triggers) {
    if (el.closest('.header-actions__wishlist')) continue;
    if (el.closest('.product-card, .card-gallery, product-info, .product-details')) continue;
    const text = `${el.getAttribute('aria-label') || ''} ${el.textContent || ''}`
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
    const className = el.getAttribute('class') || '';
    const looksLikeOpen =
      /wishlist|wish list|favorites|saved|header-btn|float/i.test(`${text} ${className} ${el.id || ''}`);
    if (!looksLikeOpen) continue;
    el.click();
    return true;
  }

  return false;
}

function bindHeaderWishlistToKaktus() {
  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('.header-actions__wishlist') : null;
      if (!trigger) return;

      const hasKaktus =
        Boolean(document.querySelector('[id*="kaktus"], [class*="kaktus"], script[src*="kaktus"]')) ||
        Boolean(window.kaktusWishlistApp) ||
        Boolean(window.kaktusAppData);

      if (!hasKaktus) return;

      event.preventDefault();
      event.stopPropagation();

      if (openKaktusWishlist()) return;

      let attempts = 0;
      const timer = window.setInterval(() => {
        attempts += 1;
        if (openKaktusWishlist() || attempts >= 20) window.clearInterval(timer);
      }, 150);
    },
    true
  );
}

/**
 * Hide Notify Me! / Restock floating "My Wishlist" launcher so it does not
 * compete with Kaktus. Never hide Kaktus UI.
 *
 * Intentionally NO document MutationObserver — Judge.me / apps mutate the DOM
 * heavily on every page and a subtree observer + case-insensitive *= scans
 * freezes the main thread ("Page Unresponsive").
 */
const THIRD_PARTY_WISHLIST_SKIP =
  'header, header-component, #header-group, .header-actions, wishlist-button, .wishlist-button, [id*="kaktus"], [class*="kaktus"]';

const THIRD_PARTY_WISHLIST_CANDIDATES =
  '[aria-label*="wishlist"], [title*="wishlist"], [class*="wishlist"], [id*="wishlist"]';

const WISHLIST_FLOATING_LABEL = /^my wishlist(\s*\d+)?$/i;

/** @type {boolean} */
let hidingThirdPartyWishlist = false;

/**
 * @param {Element} el
 * @returns {string}
 */
function floatingWishlistLabel(el) {
  const aria = el.getAttribute('aria-label');
  if (aria) return aria.replace(/\s+/g, ' ').trim();

  const title = el.getAttribute('title');
  if (title) return title.replace(/\s+/g, ' ').trim();

  let text = '';
  for (const child of el.childNodes) {
    if (child.nodeType === Node.TEXT_NODE) text += child.nodeValue || '';
  }
  return text.replace(/\s+/g, ' ').trim();
}

function hideThirdPartyFloatingWishlist() {
  if (hidingThirdPartyWishlist) return;
  hidingThirdPartyWishlist = true;

  try {
    document.querySelectorAll(THIRD_PARTY_WISHLIST_CANDIDATES).forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (el.closest(THIRD_PARTY_WISHLIST_SKIP)) return;
      const className = el.getAttribute('class') || '';
      if (/kaktus/i.test(el.id) || /kaktus/i.test(className)) return;
      if (window.getComputedStyle(el).display === 'none') return;

      const text = floatingWishlistLabel(el);
      if (!text || text.length > 48) return;
      if (!WISHLIST_FLOATING_LABEL.test(text)) return;

      let target = el;
      if (window.getComputedStyle(el).position !== 'fixed') {
        let node = el.parentElement;
        while (node && node !== document.body) {
          if (window.getComputedStyle(node).position === 'fixed') {
            target = node;
            break;
          }
          node = node.parentElement;
        }
        if (target === el) return;
      }

      const rect = target.getBoundingClientRect();
      if (rect.width > 320 || rect.height > 120) return;

      target.style.setProperty('display', 'none', 'important');
      target.setAttribute('hidden', '');
      target.setAttribute('aria-hidden', 'true');
    });
  } finally {
    hidingThirdPartyWishlist = false;
  }
}

function watchThirdPartyFloatingWishlist() {
  bindHeaderWishlistToKaktus();

  // A few delayed passes catch late-injected launchers without watching every DOM mutation.
  const delays = [0, 1000, 3000];
  for (const ms of delays) {
    window.setTimeout(hideThirdPartyFloatingWishlist, ms);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', watchThirdPartyFloatingWishlist, { once: true });
} else {
  watchThirdPartyFloatingWishlist();
}

/**
 * Kaktus stores saved products in localStorage under this key.
 * Theme hearts (e.g. quick view) read/write the same list so the app popup stays in sync.
 */
const KAKTUS_WISHLIST_KEY = 'kaktusWishlistData';
const KAKTUS_WISHLIST_CHANGE = 'parajohn:kaktus-wishlist-update';

/**
 * @returns {Array<{productId: number|string}>}
 */
function readKaktusWishlistItems() {
  try {
    const raw = localStorage.getItem(KAKTUS_WISHLIST_KEY);
    const items = raw ? JSON.parse(raw) : [];
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
}

/**
 * @param {string|number} productId
 * @returns {boolean}
 */
function isInKaktusWishlist(productId) {
  const id = Number(productId);
  if (!id) return false;
  return readKaktusWishlistItems().some((item) => Number(item?.productId) === id);
}

/**
 * @param {Array} items
 */
function writeKaktusWishlistItems(items) {
  localStorage.setItem(KAKTUS_WISHLIST_KEY, JSON.stringify(items));
  document.querySelectorAll('.kaktus-w-wishlist-counter').forEach((counter) => {
    counter.innerHTML = String(items.length);
  });
  document.dispatchEvent(
    new CustomEvent(KAKTUS_WISHLIST_CHANGE, { bubbles: true, detail: { count: items.length } })
  );
}

/**
 * Mirror Kaktus completed state on any injected add buttons for this product.
 * @param {string|number} productId
 * @param {boolean} active
 */
function syncKaktusAddButtons(productId, active) {
  const id = String(productId);
  document.querySelectorAll(`.kaktus-w-add[data-id="${id}"]`).forEach((btn) => {
    if (!(btn instanceof HTMLElement)) return;
    btn.classList.toggle('kaktus-w-completed', active);
    const btnText = btn.querySelector('.kaktus-w-wishlist-button__text');
    if (!(btnText instanceof HTMLElement)) return;
    const next = active
      ? btnText.getAttribute('data-text-completed')
      : btnText.getAttribute('data-text');
    if (next) btnText.innerText = next;
  });
}

/**
 * Prefer a real Kaktus product button click when one exists (PDP).
 * @param {string|number} productId
 * @returns {boolean}
 */
function clickKaktusAddButton(productId) {
  const btn = document.querySelector(`.kaktus-w-add[data-id="${productId}"]`);
  if (!(btn instanceof HTMLElement)) return false;
  btn.click();
  return true;
}

/**
 * Build the payload Kaktus expects from Shopify /products/{handle}.json.
 * @param {{id: number, title: string, body_html?: string, image?: {src?: string}, images?: Array<{id: number, src: string}>, variants?: Array}} product
 */
function kaktusProductFromShopify(product) {
  const images = Array.isArray(product.images) ? product.images : [];
  const getVariantImage = (imageId) => {
    if (!imageId) return '';
    const match = images.find((img) => Number(img.id) === Number(imageId));
    return match?.src || '';
  };

  const variants = (product.variants || []).map((variant) => ({
    productId: variant.id,
    variantPrice: variant.price,
    variantTitle: variant.title,
    image: getVariantImage(variant.image_id),
  }));

  return {
    productId: product.id,
    productTitle: product.title,
    description: product.body_html || '',
    description_html: product.body_html || '',
    image: product.image?.src || images[0]?.src || '',
    variants,
  };
}

/**
 * Fetch Shopify product JSON for a handle.
 * @param {string} handle
 */
async function fetchShopifyProduct(handle) {
  const clean = String(handle || '')
    .replace(/^\/products\//, '')
    .replace(/\/$/, '')
    .split('?')[0];
  if (!clean) return null;

  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}products/${encodeURIComponent(clean)}.json`);
  if (!response.ok) return null;
  const data = await response.json();
  return data?.product || null;
}

/**
 * Toggle a product in the Kaktus wishlist (same storage as the app).
 * @param {{productId?: string|number, handle?: string, title?: string}} options
 * @returns {Promise<boolean>} true when the product is wishlisted after toggle
 */
async function toggleKaktusWishlist(options = {}) {
  const productId = Number(options.productId);
  const handle = options.handle || '';

  if (productId && clickKaktusAddButton(productId)) {
    // Kaktus button owns the write; wait a tick then report resulting state.
    await new Promise((resolve) => window.setTimeout(resolve, 50));
    return isInKaktusWishlist(productId);
  }

  let product = null;
  if (handle) {
    product = await fetchShopifyProduct(handle);
  }

  if (!product && productId) {
    // Fallback: remove-only by id when fetch failed but item is already saved.
    if (isInKaktusWishlist(productId)) {
      const next = readKaktusWishlistItems().filter((item) => Number(item?.productId) !== productId);
      writeKaktusWishlistItems(next);
      syncKaktusAddButtons(productId, false);
      return false;
    }
    throw new Error('Unable to load product for wishlist');
  }

  if (!product) throw new Error('Missing product for wishlist');

  const id = Number(product.id);
  const items = readKaktusWishlistItems();
  const exists = items.some((item) => Number(item?.productId) === id);

  if (exists) {
    const next = items.filter((item) => Number(item?.productId) !== id);
    writeKaktusWishlistItems(next);
    syncKaktusAddButtons(id, false);
    return false;
  }

  const next = [...items, kaktusProductFromShopify(product)];
  writeKaktusWishlistItems(next);
  syncKaktusAddButtons(id, true);
  return true;
}

window.ParajohnKaktusWishlist = {
  key: KAKTUS_WISHLIST_KEY,
  changeEvent: KAKTUS_WISHLIST_CHANGE,
  has: isInKaktusWishlist,
  toggle: toggleKaktusWishlist,
  read: readKaktusWishlistItems,
};

/**
 * Theme heart on PDP / cards — toggles via the Kaktus localStorage bridge.
 */
class WishlistButton extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #control = null;

  /** @type {(() => void) | null} */
  #onStoreChange = null;

  connectedCallback() {
    this.#control = this.querySelector('.wishlist-button__control');
    if (!(this.#control instanceof HTMLButtonElement)) return;

    this.#syncActive();
    this.#onStoreChange = () => this.#syncActive();
    document.addEventListener(KAKTUS_WISHLIST_CHANGE, this.#onStoreChange);
    this.#control.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    if (this.#onStoreChange) {
      document.removeEventListener(KAKTUS_WISHLIST_CHANGE, this.#onStoreChange);
      this.#onStoreChange = null;
    }
    this.#control?.removeEventListener('click', this.#onClick);
  }

  #syncActive = () => {
    const productId = this.dataset.productId || '';
    const active = Boolean(productId && isInKaktusWishlist(productId));
    this.toggleAttribute('data-active', active);
    if (!(this.#control instanceof HTMLButtonElement)) return;
    this.#control.setAttribute('aria-pressed', String(active));
    const label = active
      ? this.dataset.labelRemove || this.dataset.labelAdd || ''
      : this.dataset.labelAdd || '';
    if (label) this.#control.setAttribute('aria-label', label);
  };

  #onClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!(this.#control instanceof HTMLButtonElement) || this.#control.disabled) return;

    const productId = this.dataset.productId || '';
    const handle = this.dataset.productHandle || '';
    if (!productId && !handle) return;

    this.#control.disabled = true;
    try {
      const active = await toggleKaktusWishlist({
        productId,
        handle,
        title: this.dataset.productTitle || '',
      });
      this.toggleAttribute('data-active', Boolean(active));
      this.#control.setAttribute('aria-pressed', String(Boolean(active)));
      const label = active
        ? this.dataset.labelRemove || this.dataset.labelAdd || ''
        : this.dataset.labelAdd || '';
      if (label) this.#control.setAttribute('aria-label', label);
    } catch (error) {
      console.warn('[wishlist] Toggle failed:', error);
    } finally {
      this.#control.disabled = false;
    }
  };
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
